import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Livraison',
    message: ''
  });
  const [postalCode, setPostalCode] = useState('');
  const [deliveryAvailable, setDeliveryAvailable] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // ✅ Refs pour les animations au scroll
  const pageRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // ✅ Observer pour les animations au scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (pageRef.current) {
      observer.observe(pageRef.current);
    }

    return () => {
      if (pageRef.current) {
        observer.unobserve(pageRef.current);
      }
    };
  }, []);

  const checkDelivery = () => {
    const availableZones = ['93360', '93330', '93220', '93250', '93110', '93100', '93160', '93340', '94170', '94130', '94360', '94500', '94120', '94300', '94160', '77500'];
    setDeliveryAvailable(availableZones.includes(postalCode));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Icônes SVG
  const Icons = {
    Phone: () => (
      <svg className="w-6 h-6 text-secondary transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    Email: () => (
      <svg className="w-6 h-6 text-secondary transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    Clock: () => (
      <svg className="w-6 h-6 text-secondary transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    Map: () => (
      <svg className="w-6 h-6 text-secondary transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    CheckCircle: () => (
      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    ChevronRight: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    )
  };

  const faqs = [
    { q: 'Puis-je vous contacter avant de commander ?', a: 'Bien sûr ! Notre service client est à votre disposition pour répondre à toutes vos questions avant de passer commande.' },
    { q: 'Quel est le délai de réponse ?', a: 'Nous nous engageons à vous répondre sous 24h ouvrées.' },
    { q: 'Comment signaler un problème de livraison ?', a: 'Vous pouvez nous contacter directement par téléphone ou via ce formulaire en précisant votre numéro de commande.' },
    { q: 'Puis-je modifier une commande après paiement ?', a: 'Oui, jusqu\'à la veille de la livraison. Contactez-nous rapidement avec votre numéro de commande.' },
    { q: 'Que faire si je n\'ai pas reçu l\'email de confirmation ?', a: 'Vérifiez vos spams, ou contactez-nous pour que nous vous renvoyions la confirmation.' }
  ];

  return (
    <div ref={pageRef}>
      {/* ✅ Hero Section avec animation fade-in */}
      <div className={`relative bg-gradient-to-r from-primary to-secondary py-16 transition-all duration-1000 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="container-custom">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-playfair animate-fade-in-up">Contact</h1>
            <p className="text-lg text-gray-200 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
              Une question avant de commander ? Un doute concernant la livraison ou votre commande ?
              Vous pouvez nous contacter via le formulaire.
            </p>
          </div>
        </div>
      </div>

      <div className={`py-20 bg-cream transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* ✅ Informations de contact avec animations */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-24 transition-all duration-300 hover:shadow-xl">
                <h2 className="text-2xl font-bold mb-6">Informations de contact</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4 group transition-all duration-300 hover:translate-x-2">
                    <Icons.Phone />
                    <div>
                      <h3 className="font-semibold">Téléphone</h3>
                      <p className="text-gray-600">+33 06 11 73 59 01</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 group transition-all duration-300 hover:translate-x-2">
                    <Icons.Email />
                    <div>
                      <h3 className="font-semibold">Email</h3>
                      <p className="text-gray-600">contact.aquaexpress@gmail.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 group transition-all duration-300 hover:translate-x-2">
                    <Icons.Clock />
                    <div>
                      <h3 className="font-semibold">Horaires</h3>
                      <p className="text-gray-600">Lundi-Vendredi 9:00 - 18:00</p>
                      <p className="text-gray-600">Samedi 10:00 - 19:00</p>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Formulaire de contact */}
            <div className="lg:col-span-2">
              {/* ✅ Formulaire avec animations */}
              <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-xl">
                <p className="text-gray-600 mb-6 animate-fade-in-up">
                  Merci de compléter le formulaire ci-dessous afin que nous puissions traiter votre demande efficacement.
                  Pour toute question liée à une commande existante, merci d'indiquer votre numéro de commande dans votre message.
                </p>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center animate-fade-in">
                    <div className="flex justify-center mb-3">
                      <Icons.CheckCircle />
                    </div>
                    <h3 className="text-xl font-bold text-green-600 mb-2">Message envoyé !</h3>
                    <p className="text-gray-600">Nous vous répondrons dans les plus brefs délais.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="animate-fade-in-up">
                        <label className="block text-sm font-medium mb-1">Nom</label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-secondary transition-all duration-300 focus:scale-105 focus:shadow-lg"
                        />
                      </div>
                      <div className="animate-fade-in-up animation-delay-200">
                        <label className="block text-sm font-medium mb-1">Adresse email</label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-secondary transition-all duration-300 focus:scale-105 focus:shadow-lg"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div className="animate-fade-in-up animation-delay-300">
                        <label className="block text-sm font-medium mb-1">Téléphone</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-secondary transition-all duration-300 focus:scale-105 focus:shadow-lg"
                        />
                      </div>
                      <div className="animate-fade-in-up animation-delay-400">
                        <label className="block text-sm font-medium mb-1">Sujet</label>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-secondary transition-all duration-300 focus:scale-105"
                        >
                          <option value="Livraison">Livraison</option>
                          <option value="Commande">Commande</option>
                          <option value="Produit">Produit</option>
                          <option value="Autre">Autre</option>
                        </select>
                      </div>
                    </div>

                    <div className="animate-fade-in-up animation-delay-500">
                      <label className="block text-sm font-medium mb-1">Message</label>
                      <textarea
                        name="message"
                        required
                        rows="6"
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-secondary transition-all duration-300 focus:scale-[1.02] focus:shadow-lg resize-none"
                      ></textarea>
                    </div>

                    <button 
                      type="submit" 
                      className="w-full md:w-auto px-8 py-3 bg-primary text-white rounded-full font-semibold hover:bg-opacity-90 transition-all duration-300 hover:scale-105 hover:shadow-lg animate-fade-in-up animation-delay-600"
                    >
                      Envoyer le message
                    </button>
                  </form>
                )}
              </div>

              {/* ✅ Section FAQ avec animations */}
              <div className="mt-8 bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-xl">
                <h2 className="text-xl font-bold mb-4">Questions fréquentes</h2>
                <div className="space-y-4">
                  {faqs.map((faq, idx) => (
                    <div 
                      key={idx} 
                      className="transition-all duration-300 hover:translate-x-2"
                      style={{ animationDelay: `${idx * 100}ms` }}
                    >
                      <h3 className="font-semibold text-primary hover:text-secondary transition-colors duration-300">{faq.q}</h3>
                      <p className="text-gray-600 text-sm mt-1">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ✅ CTA finale avec animation */}
              <div className="mt-8 bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-center text-white transition-all duration-500 hover:scale-105 hover:shadow-2xl">
                <h3 className="text-xl font-bold mb-2 animate-fade-in-up">Vous avez toutes les informations nécessaires pour commander ?</h3>
                <Link 
                  to="/products" 
                  className="inline-flex items-center gap-2 bg-secondary text-primary px-6 py-2 rounded-full font-semibold mt-3 hover:scale-105 transition-all duration-300 animate-fade-in-up animation-delay-200 hover:shadow-xl"
                >
                  Commander maintenant <Icons.ChevronRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;